import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {
    DimensionType,
    MeasurementInput,
    MeasurementUnit
} from "../../../API";

import { ResponsiveToolTip } from "../ResponsiveToolTip";
import { TemporaryImage } from "../../../componentsV2/image/TemporaryImage";
import UploadIcon from '@mui/icons-material/Upload';
import { UploadStagingImageCarousel } from "../../../componentsV2/image/UploadStagingImageCarousel";
import { isMobile } from "react-device-detect";
import { useIssues } from "../contexts/IssuesContextProvider";
import { useLocations } from "../contexts/LocationContextProvider";
import { useSnackbar } from "notistack";
import { useState } from "react";

type CreateLocationDialogProps = {
    open: boolean;
    onClose: () => void;
};

export const CreateIssueDialog = ({ open, onClose }: CreateLocationDialogProps) => {
    const { locations } = useLocations();
    const { submitIssue } = useIssues();
    const [selectedLocationId, setSelectedLocationid] = useState<string | null>(null);
    const [summary, setSummary] = useState<string>("");
    const [stagedImages, setStagedImages] = useState<Array<TemporaryImage>>([]);
    const [width, setWidth] = useState<number>();
    const [length, setLength] = useState<number>();
    const [unit, setUnit] = useState<MeasurementUnit>(MeasurementUnit.FOOT);
    const snackbar = useSnackbar();

    const submit = async () => {
        try {
            if (!submitIssue) {
                throw new Error("submitIssue is not defined");
            }
            const measurement: MeasurementInput = (length || width) ?
                {
                    length: length,
                    width: width,
                    measurementUnit: unit,
                    dimensionType: (!width || !length) ? DimensionType.LINEAR : DimensionType.SQUARE
                } : {};
            submitIssue(
                selectedLocationId as string,
                summary,
                stagedImages.map(image => new File([image.file], image.id, { type: image.file.type })),
                measurement
            ).then(() => {
                snackbar.enqueueSnackbar(`Issue Created successfully.`, { variant: "success" });
            }).catch((error) => {
                snackbar.enqueueSnackbar("Failed to create Issue", { variant: "error" });
            });
            snackbar.enqueueSnackbar(`Issue Submitted, it will appear in dashboard shortly.`, { variant: "success" });
        } finally {
            closeDialog();
        }
    };

    const closeDialog = () => {
        onClose();
        setSummary("");
        setSelectedLocationid(null);
        setStagedImages([]);
    };

    return (
        <Dialog
            open={open}
            onClose={closeDialog}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
        >
            <DialogTitle>Submit an Issue</DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="body1" color="textSecondary">
                        Issue Location:
                    </Typography>
                    <TextField
                        autoFocus
                        select
                        label="Location"
                        value={selectedLocationId}
                        onChange={(e) => setSelectedLocationid(e.target.value)}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        required
                    >
                        {locations.map((location) => (
                            <MenuItem key={location.id} value={location.id}>
                                {location.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                {/* Grid needed to prevent image carousel from overflowing the dialog */}
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                width: "100%",
                                aspectRatio: "16/9",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}
                        >
                            <UploadStagingImageCarousel
                                imageDTOs={stagedImages}
                                setImageDTOs={setStagedImages}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Box display="flex" flexDirection="column">
                    <Typography variant="body1" color="textSecondary">
                        Issue Details:
                    </Typography>
                    <TextField
                        margin="dense"
                        placeholder="Please provide details about the issue you are reporting."
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        required
                        multiline
                        rows={4}
                    />
                </Box>
                <Box display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="body1" color="textSecondary">
                        Measurement:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <TextField
                            size="small"
                            type="number"
                            id="length"
                            name="length"
                            label="Length"
                            variant="outlined"
                            sx={{ minWidth: "100px", flex: 1 }}
                            inputProps={{ min: 0 }}
                            onChange={(e) => setLength(Number(e.target.value))}
                        />
                        <TextField
                            size="small"
                            type="number"
                            id="width"
                            name="width"
                            label="Width"
                            variant="outlined"
                            sx={{ minWidth: "100px", flex: 1 }}
                            inputProps={{ min: 0 }}
                            onChange={(e) => setWidth(Number(e.target.value))}
                        />
                        <FormControl sx={{ minWidth: 120, flex: 1 }} size="small">
                            <InputLabel>Unit</InputLabel>
                            <Select
                                id="unit"
                                name="unit"
                                native
                                sx={{ minWidth: "100px" }}
                                onChange={(e) => setUnit(e.target.value as MeasurementUnit)}
                                value={unit}
                                label="Unit"
                            >
                                {Object.values(MeasurementUnit).map((unitValue) => (
                                    <option key={unitValue} value={unitValue}>
                                        {unitValue}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={closeDialog}
                    variant="outlined"
                    color="error"
                >
                    Cancel
                </Button>
                <ResponsiveToolTip title={!selectedLocationId ? "Location is required" : ""}>
                    <span>
                        <Button
                            variant="contained"
                            onClick={submit}
                            startIcon={<UploadIcon />}
                            disabled={!selectedLocationId || !summary}
                        >
                            Submit
                        </Button>
                    </span>
                </ResponsiveToolTip>
            </DialogActions>
        </Dialog>
    );
};